// Admin — Atlas IA (unified page) v2
//
// 2026-10-01 restructure 2:
//  - Removed the "Fiches à créer" tab (DeepSeek batch moved to Thème du mois)
//  - Catalogue now hits the new /api/admin/atlas-ia/catalogue endpoint
//    which returns ALL fiches (no limit) with per-language presence
//    flags.
//  - Row click → detail modal with FR/EN/ES/PT inner tabs, inline
//    edit per lang (PATCH), related fiches (deduced + manual), and
//    a one-click "Traduire manquants" button.
//  - Bulk checkbox + "Traduire la sélection" → fires DeepSeek on the
//    chosen fiches via /translate-bulk.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Shield, Inbox, Database, History, Search, Loader2, X, Save, Edit, Languages, Link2, Trash2, Plus, Check, GitMerge, AlertTriangle } from "lucide-react";
import AdminAtlasClaimsPage from "./AdminAtlasClaimsPage";
import AdminInboundSuggestionsPage from "./AdminInboundSuggestionsPage";
import { adminApi } from "@/lib/adminApi";

const TABS = [
  { id: "catalogue", label: "Catalogue", icon: Database },
  { id: "duplicates", label: "Doublons", icon: GitMerge },
  { id: "claims", label: "Claims", icon: Shield },
  { id: "suggestions", label: "Suggestions", icon: Inbox },
  { id: "legacy", label: "Legacy", icon: History },
];

const KIND_LABELS = { appellation: "Appellation", cepage: "Cépage", producer: "Producteur" };
const LANGS = ["fr", "en", "es", "pt"];

const AdminAtlasIAPage = () => {
  const [tab, setTab] = useState("catalogue");
  return (
    <div className="max-w-7xl mx-auto p-6 md:p-10" data-testid="admin-atlas-ia">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Admin · Inbound</div>
        <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)]">Atlas IA</h1>
        <p className="text-[13.5px] text-[color:var(--gv-ink)]/65 mt-1 max-w-2xl">
          Toutes les fiches encyclopédiques (appellations, cépages, producteurs). Clique une ligne pour la lire ou l'éditer dans les 4 langues. Sélectionne plusieurs lignes pour traduire en masse.
        </p>
      </div>
      <div className="flex gap-2 border-b border-[color:var(--gv-line)] mb-6 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm transition-colors border-b-2 -mb-px flex items-center gap-1.5 whitespace-nowrap ${
                tab === t.id
                  ? "border-[color:var(--gv-pink)] text-[color:var(--gv-ink)] font-medium"
                  : "border-transparent text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)]"
              }`}
              data-testid={`atlas-tab-${t.id}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "catalogue" && <CataloguePanel />}
      {tab === "duplicates" && <DuplicatesPanel />}
      {tab === "claims" && (<div className="-mx-6 md:-mx-10"><AdminAtlasClaimsPage /></div>)}
      {tab === "suggestions" && (<div className="-mx-6 md:-mx-10"><AdminInboundSuggestionsPage /></div>)}
      {tab === "legacy" && <LegacyPanel />}
    </div>
  );
};

// ────────────────────── Catalogue ──────────────────────

function CataloguePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [lang, setLang] = useState("all"); // filter by MISSING a lang
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set()); // "kind:slug"
  const [openFiche, setOpenFiche] = useState(null); // { kind, slug }
  const [bulkBusy, setBulkBusy] = useState(false);
  const [flash, setFlash] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminApi.atlasCatalogue();
      setItems(d?.items || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const qLow = q.trim().toLowerCase();
    return items.filter((r) => {
      if (kind !== "all" && r.kind !== kind) return false;
      if (status !== "all" && r.status !== status) return false;
      if (lang !== "all") {
        // Filter: only fiches MISSING this language
        if (r.langs_present?.[lang]) return false;
      }
      if (qLow) {
        const hay = `${r.name} ${r.region} ${r.slug}`.toLowerCase();
        if (!hay.includes(qLow)) return false;
      }
      return true;
    });
  }, [items, kind, status, lang, q]);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); }, [kind, status, lang, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageEnd);

  const allSelectedOnPage = pageItems.length > 0 && pageItems.every((r) => selected.has(`${r.kind}:${r.slug}`));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelectedOnPage) {
      pageItems.forEach((r) => next.delete(`${r.kind}:${r.slug}`));
    } else {
      pageItems.forEach((r) => next.add(`${r.kind}:${r.slug}`));
    }
    setSelected(next);
  };
  const selectAllFiltered = () => {
    const next = new Set(selected);
    filtered.forEach((r) => next.add(`${r.kind}:${r.slug}`));
    setSelected(next);
  };
  const toggleOne = (r) => {
    const k = `${r.kind}:${r.slug}`;
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    setSelected(next);
  };

  const bulkTranslate = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Traduire ${selected.size} fiche${selected.size > 1 ? "s" : ""} en EN/ES/PT via DeepSeek ?\n(coûts: ~1-2k tokens par fiche × 3 langues)`)) return;
    setBulkBusy(true); setFlash("");
    try {
      const payload = {
        items: [...selected].map((k) => {
          const [kd, sl] = k.split(":"); return { kind: kd, slug: sl };
        }),
        target_langs: ["en", "es", "pt"],
      };
      const r = await adminApi.atlasTranslateBulk(payload);
      setFlash(`${r.created} traduction(s) créée(s) · ${r.processed} fiche(s) traitée(s)${r.errors?.length ? ` · ${r.errors.length} erreur(s)` : ""}`);
      setSelected(new Set());
      await reload();
    } catch (e) {
      setFlash(`Erreur: ${e.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  // Selected proposed items (status = 'proposed') need FR + translations
  // generated from scratch via DeepSeek. We derive this list from the
  // current `items` state since `selected` only stores "kind:slug".
  const selectedProposed = useMemo(() => {
    if (selected.size === 0) return [];
    const bySlug = new Map(items.map((r) => [`${r.kind}:${r.slug}`, r]));
    return [...selected]
      .map((k) => bySlug.get(k))
      .filter((r) => r && r.status === "proposed");
  }, [selected, items]);

  // Rows actually selected (for the merge button). Merging is only
  // allowed when the 2+ selected rows share the same kind.
  const selectedRows = useMemo(() => {
    if (selected.size === 0) return [];
    const bySlug = new Map(items.map((r) => [`${r.kind}:${r.slug}`, r]));
    return [...selected].map((k) => bySlug.get(k)).filter(Boolean);
  }, [selected, items]);
  const canMerge = selectedRows.length >= 2 &&
    selectedRows.every((r) => r.kind === selectedRows[0].kind);
  const [mergeModal, setMergeModal] = useState(null);
  const openMerge = () => {
    if (!canMerge) return;
    // Default primary: the row with the most lang coverage.
    const scored = [...selectedRows].sort((a, b) => {
      const sa = Object.values(a.langs_present || {}).filter(Boolean).length;
      const sb = Object.values(b.langs_present || {}).filter(Boolean).length;
      if (sa !== sb) return sb - sa;
      return (a.slug || "").localeCompare(b.slug || "");
    });
    setMergeModal({ kind: scored[0].kind, rows: scored, primarySlug: scored[0].slug });
  };

  const bulkGenerate = async () => {
    if (selectedProposed.length === 0) return;
    if (!window.confirm(`Générer ${selectedProposed.length} fiche${selectedProposed.length > 1 ? "s" : ""} proposée${selectedProposed.length > 1 ? "s" : ""} dans les 4 langues (FR + EN/ES/PT) via DeepSeek ?\n\nCoûts estimés: ~4-6k tokens par fiche. Les fiches qui ont déjà une version FR seront ignorées.`)) return;
    setBulkBusy(true); setFlash("");
    try {
      const payload = {
        items: selectedProposed.map((r) => ({
          kind: r.kind, slug: r.slug,
          name: r.name, region: r.region, country: r.country,
        })),
        translate: true,
      };
      const r = await adminApi.atlasGenerateBulk(payload);
      setFlash(`${r.created_fr} fiche(s) FR créée(s) · ${r.created_translations} traduction(s) · ${r.processed} fiche(s) traitée(s)${r.errors?.length ? ` · ${r.errors.length} erreur(s)` : ""}`);
      setSelected(new Set());
      await reload();
    } catch (e) {
      setFlash(`Erreur: ${e.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="catalogue-panel">
      {/* Filters */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 block mb-1">Recherche</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--gv-ink)]/40" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Nom, région, slug…"
              className="w-full pl-8 pr-3 py-2 border border-[color:var(--gv-line)] rounded-[5px] text-[13.5px] focus:border-[color:var(--gv-pink)] focus:outline-none"
              data-testid="catalogue-search" />
          </div>
        </div>
        <SelectField label="Type" value={kind} onChange={setKind} testid="catalogue-kind"
          options={[["all","Tous"],["appellation","Appellations"],["cepage","Cépages"],["producer","Producteurs"]]} />
        <SelectField label="Statut" value={status} onChange={setStatus} testid="catalogue-status"
          options={[["all","Tous"],["existing","Existant"],["claimed","Claimed"],["proposed","Proposé"]]} />
        <SelectField label="Langue manquante" value={lang} onChange={setLang} testid="catalogue-lang"
          options={[["all","—"],["en","EN manquante"],["es","ES manquante"],["pt","PT manquante"]]} />
        <div className="text-[12.5px] text-[color:var(--gv-ink)]/60 ml-auto">
          {loading ? <Loader2 size={13} className="animate-spin inline"/> : `${filtered.length} / ${items.length} fiche${items.length>1?"s":""}`}
        </div>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="bg-[color:var(--gv-pink)]/10 border border-[color:var(--gv-pink)]/30 rounded-[6px] px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="bulk-bar">
          <div className="text-[13px] text-[color:var(--gv-ink)]">
            <strong>{selected.size}</strong> fiche{selected.size>1?"s":""} sélectionnée{selected.size>1?"s":""}
            {selectedProposed.length > 0 && (
              <span className="ml-2 text-[12px] text-amber-700">· dont <strong>{selectedProposed.length}</strong> proposée{selectedProposed.length>1?"s":""} (à générer)</span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setSelected(new Set())} className="text-[12.5px] px-3 py-1.5 border border-[color:var(--gv-line)] rounded-[4px] hover:bg-white" data-testid="bulk-clear">
              Désélectionner
            </button>
            {canMerge && (
              <button onClick={openMerge}
                className="text-[12.5px] px-3 py-1.5 bg-indigo-600 text-white rounded-[4px] hover:opacity-90 inline-flex items-center gap-1.5"
                data-testid="bulk-merge">
                <GitMerge size={12}/> Fusionner ({selectedRows.length})
              </button>
            )}
            {selectedProposed.length > 0 && (
              <button onClick={bulkGenerate} disabled={bulkBusy}
                className="text-[12.5px] px-3 py-1.5 bg-amber-600 text-white rounded-[4px] hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="bulk-generate">
                {bulkBusy ? <Loader2 size={12} className="animate-spin"/> : <Plus size={12}/>}
                Générer ({selectedProposed.length}) dans 4 langues
              </button>
            )}
            <button onClick={bulkTranslate} disabled={bulkBusy}
              className="text-[12.5px] px-3 py-1.5 bg-[color:var(--gv-pink)] text-white rounded-[4px] hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="bulk-translate">
              {bulkBusy ? <Loader2 size={12} className="animate-spin"/> : <Languages size={12}/>}
              Traduire en EN/ES/PT
            </button>
          </div>
        </div>
      )}
      {flash && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12.5px] px-3 py-2 rounded-[5px]">{flash}</div>}

      {mergeModal && (
        <MergeModal
          modal={mergeModal}
          setPrimary={(slug) => setMergeModal({ ...mergeModal, primarySlug: slug })}
          onClose={() => setMergeModal(null)}
          onDone={async (res) => {
            setMergeModal(null);
            setFlash(`Fusion OK — primary: ${res.primary_slug} · ${res.local_docs_removed} docs supprimés · ${res.variants_merged.length} variantes conservées`);
            setSelected(new Set());
            await reload();
          }}
        />
      )}

      {/* Table */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="animate-spin inline text-[color:var(--gv-pink)]"/></div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-[13px] text-[color:var(--gv-ink)]/55">Aucune fiche ne correspond aux filtres.</div>
        ) : (
          <table className="w-full text-[13.5px]">
            <thead className="bg-[color:var(--gv-mist)]/40 text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">
              <tr>
                <th className="px-3 py-2.5 w-10">
                  <input type="checkbox" checked={allSelectedOnPage} onChange={toggleAll} className="accent-[color:var(--gv-pink)]" data-testid="select-all"/>
                </th>
                <th className="text-left px-3 py-2.5">Nom</th>
                <th className="text-left px-3 py-2.5">Type</th>
                <th className="text-left px-3 py-2.5">Région</th>
                <th className="text-left px-3 py-2.5">Langues</th>
                <th className="text-left px-3 py-2.5">Statut</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((r) => {
                const k = `${r.kind}:${r.slug}`;
                return (
                  <tr key={k} className="border-t border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)]/20" data-testid={`row-${k}`}>
                    <td className="px-3 py-2.5">
                      <input type="checkbox" checked={selected.has(k)} onChange={() => toggleOne(r)} onClick={(e)=>e.stopPropagation()} className="accent-[color:var(--gv-pink)]" data-testid={`select-${k}`}/>
                    </td>
                    <td className="px-3 py-2.5 font-medium cursor-pointer" onClick={() => setOpenFiche({ kind: r.kind, slug: r.slug })} data-testid={`open-${k}`}>
                      <span className="text-[color:var(--gv-ink)] hover:text-[color:var(--gv-pink)]">{r.name}</span>
                    </td>
                    <td className="px-3 py-2.5"><span className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">{KIND_LABELS[r.kind] || r.kind}</span></td>
                    <td className="px-3 py-2.5 text-[color:var(--gv-ink)]/70">{r.region}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex gap-1">
                        {LANGS.map((lg) => (
                          <span key={lg} className={`text-[10px] px-1.5 py-0.5 rounded ${r.langs_present?.[lg] ? "bg-emerald-100 text-emerald-700" : "bg-[color:var(--gv-ink)]/5 text-[color:var(--gv-ink)]/30"}`}>{lg.toUpperCase()}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded uppercase tracking-widest ${
                        r.status === "existing" ? "bg-emerald-100 text-emerald-700"
                        : r.status === "claimed" ? "bg-blue-100 text-blue-700"
                        : "bg-amber-100 text-amber-700"
                      }`}>{r.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {/* Pagination */}
        {filtered.length > PAGE_SIZE && (
          <Pagination
            page={safePage}
            totalPages={totalPages}
            total={filtered.length}
            pageStart={pageStart}
            pageEnd={Math.min(pageEnd, filtered.length)}
            onPage={setPage}
            onSelectAllFiltered={selectAllFiltered}
            selectedCount={selected.size}
          />
        )}
      </div>

      {openFiche && <FicheModal {...openFiche} items={items} onClose={async (changed) => { setOpenFiche(null); if (changed) await reload(); }} />}
    </div>
  );
}

function Pagination({ page, totalPages, total, pageStart, pageEnd, onPage, onSelectAllFiltered, selectedCount }) {
  // Build a compact page-number list: 1, …, p-1, p, p+1, …, total
  const pages = [];
  const push = (p) => { if (!pages.includes(p) && p >= 1 && p <= totalPages) pages.push(p); };
  push(1); push(2);
  push(page - 1); push(page); push(page + 1);
  push(totalPages - 1); push(totalPages);
  pages.sort((a, b) => a - b);
  const items = [];
  let last = 0;
  for (const p of pages) {
    if (p - last > 1) items.push({ gap: true, key: `gap-${p}` });
    items.push({ page: p, key: `p-${p}` });
    last = p;
  }
  return (
    <div className="px-4 py-3 border-t border-[color:var(--gv-line)] flex items-center justify-between gap-3 flex-wrap text-[12.5px] text-[color:var(--gv-ink)]/70">
      <div>
        {pageStart + 1}–{pageEnd} sur {total}
        {" · "}
        <button onClick={onSelectAllFiltered} className="text-[color:var(--gv-pink)] hover:underline" data-testid="select-all-filtered">
          sélectionner tout ({total}){selectedCount > 0 && ` — déjà ${selectedCount} sélectionné${selectedCount>1?"s":""}`}
        </button>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className="px-2.5 py-1 border border-[color:var(--gv-line)] rounded-[4px] hover:bg-[color:var(--gv-mist)] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="page-prev"
        >‹</button>
        {items.map((it) => it.gap ? (
          <span key={it.key} className="px-2 text-[color:var(--gv-ink)]/40">…</span>
        ) : (
          <button
            key={it.key}
            onClick={() => onPage(it.page)}
            className={`min-w-[32px] px-2 py-1 rounded-[4px] ${it.page === page ? "bg-[color:var(--gv-pink)] text-white" : "border border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)]"}`}
            data-testid={`page-${it.page}`}
          >{it.page}</button>
        ))}
        <button
          onClick={() => onPage(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="px-2.5 py-1 border border-[color:var(--gv-line)] rounded-[4px] hover:bg-[color:var(--gv-mist)] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="page-next"
        >›</button>
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 block mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="border border-[color:var(--gv-line)] rounded-[5px] px-2.5 py-2 text-[13.5px] bg-white focus:border-[color:var(--gv-pink)] focus:outline-none" data-testid={testid}>
        {options.map(([v,l]) => (<option key={v} value={v}>{l}</option>))}
      </select>
    </div>
  );
}

// ────────────────────── Fiche Modal ──────────────────────

function FicheModal({ kind, slug, items, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState("fr");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [changed, setChanged] = useState(false);
  const [showLinker, setShowLinker] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminApi.atlasFiche(kind, slug);
      setData(d);
      setDraft(d?.variants?.[activeLang] || {});
    } finally { setLoading(false); }
  }, [kind, slug, activeLang]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // When switching lang, reload draft from the fetched variants.
    setDraft(data?.variants?.[activeLang] || {});
    setEditing(false);
  }, [activeLang, data]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: draft.name, region: draft.region, country: draft.country,
        description: draft.description, terroir: draft.terroir, philosophy: draft.philosophy,
        cahier_des_charges_key_rules: draft.cahier_des_charges_key_rules,
        food_pairings: draft.food_pairings,
        grapes: draft.grapes, aromatic_profile: draft.aromatic_profile,
        origin: draft.origin, appellation: draft.appellation,
      };
      // Strip undefined
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      await adminApi.atlasFichePatch(kind, slug, activeLang, payload);
      setEditing(false); setChanged(true);
      await load();
    } finally { setSaving(false); }
  };

  const translateMissing = async () => {
    const missing = LANGS.filter((l) => l !== "fr" && !data?.variants?.[l]);
    if (missing.length === 0) { window.alert("Toutes les traductions sont déjà présentes."); return; }
    setTranslating(true);
    try {
      await adminApi.atlasTranslateBulk({ items: [{ kind, slug }], target_langs: missing });
      setChanged(true);
      await load();
    } finally { setTranslating(false); }
  };

  const addLink = async (link) => {
    await adminApi.atlasFicheLink(kind, slug, link);
    setChanged(true);
    await load();
    setShowLinker(false);
  };
  const removeLink = async (link) => {
    await adminApi.atlasFicheUnlink(kind, slug, link);
    setChanged(true);
    await load();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="fiche-modal">
      <div className="bg-white rounded-[6px] w-full max-w-4xl mt-10 mb-10 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[color:var(--gv-line)]">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1">{KIND_LABELS[kind]} · {slug}</div>
            <h3 className="font-display text-2xl text-[color:var(--gv-ink)]">{data?.variants?.fr?.name || data?.upstream?.name || slug}</h3>
          </div>
          <button onClick={() => onClose(changed)} className="text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)]" data-testid="modal-close"><X size={18}/></button>
        </div>

        {loading ? (
          <div className="p-12 text-center"><Loader2 className="animate-spin inline text-[color:var(--gv-pink)]"/></div>
        ) : (
          <>
            {/* Lang tabs */}
            <div className="flex gap-1 px-5 pt-4 border-b border-[color:var(--gv-line)]">
              {LANGS.map((lg) => {
                const present = !!data?.variants?.[lg];
                return (
                  <button key={lg} onClick={() => setActiveLang(lg)}
                    className={`px-3 py-1.5 text-[12px] uppercase tracking-widest rounded-t-[4px] border border-b-0 ${
                      activeLang === lg
                        ? "bg-white border-[color:var(--gv-line)] text-[color:var(--gv-pink)] font-semibold"
                        : "border-transparent text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)]"
                    }`}
                    data-testid={`lang-tab-${lg}`}
                  >
                    {lg}
                    {!present && <span className="ml-1 text-[9px] text-amber-600">·vide</span>}
                  </button>
                );
              })}
              <div className="ml-auto flex gap-2 mb-1">
                <button onClick={translateMissing} disabled={translating}
                  className="text-[12px] px-3 py-1.5 border border-[color:var(--gv-line)] rounded-[4px] hover:bg-[color:var(--gv-mist)] inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="translate-missing">
                  {translating ? <Loader2 size={12} className="animate-spin"/> : <Languages size={12}/>} Traduire manquants
                </button>
                {!editing ? (
                  <button onClick={() => setEditing(true)} className="text-[12px] px-3 py-1.5 bg-[color:var(--gv-ink)] text-white rounded-[4px] hover:opacity-90 inline-flex items-center gap-1.5" data-testid="edit-start"><Edit size={12}/> Éditer</button>
                ) : (
                  <>
                    <button onClick={() => { setEditing(false); setDraft(data?.variants?.[activeLang] || {}); }} className="text-[12px] px-3 py-1.5 border border-[color:var(--gv-line)] rounded-[4px] hover:bg-[color:var(--gv-mist)]" data-testid="edit-cancel">Annuler</button>
                    <button onClick={save} disabled={saving} className="text-[12px] px-3 py-1.5 bg-[color:var(--gv-pink)] text-white rounded-[4px] hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="edit-save">
                      {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Enregistrer
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {!data?.variants?.[activeLang] && !editing && (
                <div className="bg-amber-50 border border-amber-200 text-amber-700 text-[12.5px] px-3 py-2 rounded-[5px]">
                  Pas encore de contenu pour la langue <strong>{activeLang.toUpperCase()}</strong>. Clique "Traduire manquants" ou "Éditer" pour en créer un.
                </div>
              )}
              <FicheFields kind={kind} draft={draft} setDraft={setDraft} editing={editing} />

              {/* Related fiches */}
              <div className="pt-4 border-t border-[color:var(--gv-line)]">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-[14px] text-[color:var(--gv-ink)] flex items-center gap-2"><Link2 size={14}/> Fiches liées</h4>
                  <button onClick={() => setShowLinker(!showLinker)} className="text-[12px] text-[color:var(--gv-pink)] hover:underline inline-flex items-center gap-1" data-testid="linker-toggle">
                    <Plus size={12}/> Ajouter un lien
                  </button>
                </div>
                {showLinker && <Linker items={items} onPick={addLink} />}
                <RelatedBlock title="Déduites" entries={data?.related_fiches?.deduced || []} />
                <RelatedBlock title="Manuelles" entries={data?.related_fiches?.manual || []} onRemove={removeLink} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function FicheFields({ kind, draft, setDraft, editing }) {
  const F = (k) => draft?.[k] || "";
  const S = (k, v) => setDraft({ ...draft, [k]: v });
  if (editing) {
    return (
      <div className="space-y-3">
        <Field label="Nom" value={F("name")} onChange={(v) => S("name", v)} testid="fld-name"/>
        {kind !== "cepage" && <Field label="Région" value={F("region")} onChange={(v) => S("region", v)} testid="fld-region"/>}
        {kind !== "cepage" && <Field label="Pays" value={F("country")} onChange={(v) => S("country", v)} testid="fld-country"/>}
        {kind === "cepage" && <Field label="Origine" value={F("origin")} onChange={(v) => S("origin", v)} testid="fld-origin"/>}
        {kind === "producer" && <Field label="Appellation" value={F("appellation")} onChange={(v) => S("appellation", v)} testid="fld-appellation"/>}
        <Textarea label="Description" value={F("description")} onChange={(v) => S("description", v)} testid="fld-description"/>
        {kind === "appellation" && <Textarea label="Terroir" value={F("terroir")} onChange={(v) => S("terroir", v)} testid="fld-terroir"/>}
        {kind === "appellation" && <Textarea label="Philosophie" value={F("philosophy")} onChange={(v) => S("philosophy", v)} testid="fld-philosophy"/>}
        {kind === "cepage" && <Field label="Profil aromatique" value={F("aromatic_profile")} onChange={(v) => S("aromatic_profile", v)} testid="fld-aromatic"/>}
      </div>
    );
  }
  return (
    <div className="space-y-3 text-[13.5px] text-[color:var(--gv-ink)]/85 leading-relaxed">
      {F("region") && <ReadRow label="Région" value={F("region")}/>}
      {F("country") && <ReadRow label="Pays" value={F("country")}/>}
      {F("origin") && <ReadRow label="Origine" value={F("origin")}/>}
      {F("appellation") && <ReadRow label="Appellation" value={F("appellation")}/>}
      {F("description") && <ReadRow label="Description" value={F("description")}/>}
      {F("terroir") && <ReadRow label="Terroir" value={F("terroir")}/>}
      {F("philosophy") && <ReadRow label="Philosophie" value={F("philosophy")}/>}
      {F("aromatic_profile") && <ReadRow label="Profil aromatique" value={F("aromatic_profile")}/>}
      {(draft?.cahier_des_charges_key_rules || []).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Cahier des charges — règles clés</div>
          <ul className="list-disc pl-5 space-y-1">{draft.cahier_des_charges_key_rules.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </div>
      )}
      {(draft?.food_pairings || []).length > 0 && (
        <ReadRow label="Accords" value={draft.food_pairings.join(" · ")}/>
      )}
      {(draft?.grapes || []).length > 0 && (
        <ReadRow label="Cépages" value={draft.grapes.join(" · ")}/>
      )}
    </div>
  );
}

function ReadRow({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-0.5">{label}</div>
      <div className="whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 block mb-1">{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-[13.5px] focus:border-[color:var(--gv-pink)] focus:outline-none" data-testid={testid}/>
    </div>
  );
}
function Textarea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 block mb-1">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4}
        className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-[13.5px] focus:border-[color:var(--gv-pink)] focus:outline-none" data-testid={testid}/>
    </div>
  );
}

function RelatedBlock({ title, entries, onRemove }) {
  if (!entries?.length) return null;
  return (
    <div className="mt-2">
      <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">{title}</div>
      <div className="flex flex-wrap gap-2">
        {entries.map((e, i) => (
          <span key={`${e.kind}:${e.slug}:${i}`} className="inline-flex items-center gap-1.5 text-[12.5px] bg-[color:var(--gv-mist)] px-2 py-1 rounded-[4px]">
            <span className="text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/60">{e.kind}</span>
            <span className="font-medium text-[color:var(--gv-ink)]">{e.name || e.slug}</span>
            {onRemove && (
              <button onClick={() => onRemove({ target_kind: e.kind, target_slug: e.slug })} className="text-rose-500 hover:text-rose-700 ml-1" data-testid={`link-remove-${e.slug}`}>
                <Trash2 size={11}/>
              </button>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

function Linker({ items, onPick }) {
  const [q, setQ] = useState("");
  const matches = useMemo(() => {
    const qLow = q.trim().toLowerCase();
    if (qLow.length < 2) return [];
    return items.filter((r) => (r.name + " " + r.slug).toLowerCase().includes(qLow)).slice(0, 10);
  }, [q, items]);
  return (
    <div className="bg-[color:var(--gv-mist)]/40 border border-[color:var(--gv-line)] rounded-[5px] p-3 mb-3">
      <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Chercher une fiche à lier…"
        className="w-full border border-[color:var(--gv-line)] rounded-[5px] px-3 py-2 text-[13px] focus:border-[color:var(--gv-pink)] focus:outline-none mb-2" data-testid="linker-input"/>
      {matches.map((r) => (
        <button key={`${r.kind}:${r.slug}`} onClick={() => onPick({ target_kind: r.kind, target_slug: r.slug, target_name: r.name })}
          className="block w-full text-left px-3 py-1.5 text-[12.5px] hover:bg-white rounded-[4px]" data-testid={`linker-pick-${r.slug}`}>
          <span className="text-[10px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mr-2">{r.kind}</span>
          <strong>{r.name}</strong>
          <span className="text-[color:var(--gv-ink)]/55 ml-2">{r.region}</span>
        </button>
      ))}
    </div>
  );
}

// ────────────────────── Legacy panel ──────────────────────

function LegacyPanel() {
  const links = [
    { to: "/admin/app-claims", label: "Claims (legacy)", desc: "Anciens claims producteurs stockés dans la base app." },
    { to: "/admin/app-atlas", label: "Atlas · IA (legacy)", desc: "Ancien générateur Atlas IA (pré-DeepSeek)." },
    { to: "/admin/producer-leads", label: "Fiches à créer (legacy)", desc: "Ancien queue des leads producteurs." },
  ];
  return (
    <div className="space-y-3" data-testid="legacy-panel">
      <p className="text-[13px] text-[color:var(--gv-ink)]/65">
        Ces pages contiennent encore des enregistrements créés avant la restructure 2026-10. Elles restent accessibles pour nettoyer les archives.
      </p>
      <div className="grid md:grid-cols-3 gap-3">
        {links.map((l) => (
          <Link key={l.to} to={l.to} className="block bg-white border border-[color:var(--gv-line)] rounded-[6px] p-4 hover:border-[color:var(--gv-pink)] transition-colors" data-testid={`legacy-link-${l.to.replace(/\W+/g,"-")}`}>
            <div className="font-medium text-[14px] text-[color:var(--gv-ink)]">{l.label}</div>
            <div className="text-[12.5px] text-[color:var(--gv-ink)]/60 mt-1">{l.desc}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ────────────────────── Duplicates panel ──────────────────────

function DuplicatesPanel() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mergeModal, setMergeModal] = useState(null);
  const [flash, setFlash] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminApi.atlasDuplicates();
      setGroups(d?.groups || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openMerge = (g) => {
    setMergeModal({
      kind: g.kind,
      rows: g.all_rows,
      primarySlug: g.primary.slug,
    });
  };

  return (
    <div className="space-y-4" data-testid="duplicates-panel">
      <div className="bg-amber-50 border border-amber-200 rounded-[6px] p-4 text-[13px] text-amber-800">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0"/>
          <div>
            <strong>{groups.length}</strong> groupe{groups.length>1?"s":""} de doublons détecté{groups.length>1?"s":""} par nom normalisé. Ouvre un groupe pour choisir la fiche primaire et fusionner.
          </div>
        </div>
      </div>
      {flash && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12.5px] px-3 py-2 rounded-[5px]">{flash}</div>}

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="animate-spin inline text-[color:var(--gv-pink)]"/></div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-8 text-center text-[13px] text-[color:var(--gv-ink)]/55">Aucun doublon détecté 🎉</div>
      ) : (
        <div className="space-y-3">
          {groups.map((g, i) => (
            <div key={i} className="bg-white border border-[color:var(--gv-line)] rounded-[6px] overflow-hidden" data-testid={`dup-group-${i}`}>
              <div className="px-4 py-3 border-b border-[color:var(--gv-line)] flex items-center justify-between flex-wrap gap-2 bg-[color:var(--gv-mist)]/30">
                <div>
                  <span className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mr-2">{KIND_LABELS[g.kind] || g.kind}</span>
                  <strong className="text-[14px]">{g.primary.name}</strong>
                  <span className="text-[12px] text-[color:var(--gv-ink)]/55 ml-2">· {g.all_rows.length} fiches similaires</span>
                </div>
                <button onClick={() => openMerge(g)} className="text-[12.5px] px-3 py-1.5 bg-indigo-600 text-white rounded-[4px] hover:opacity-90 inline-flex items-center gap-1.5" data-testid={`dup-merge-${i}`}>
                  <GitMerge size={12}/> Fusionner
                </button>
              </div>
              <table className="w-full text-[13px]">
                <tbody>
                  {g.all_rows.map((r, j) => (
                    <tr key={r.slug} className="border-t border-[color:var(--gv-line)]">
                      <td className="px-4 py-2 w-24 text-[11px] uppercase tracking-widest">
                        {r.slug === g.primary.slug ? (
                          <span className="text-emerald-700">Primaire</span>
                        ) : (
                          <span className="text-[color:var(--gv-ink)]/40">Doublon</span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-medium">{r.name}</td>
                      <td className="px-4 py-2 text-[color:var(--gv-ink)]/60 text-[12.5px]">{r.region}</td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1">
                          {LANGS.map((lg) => (
                            <span key={lg} className={`text-[10px] px-1.5 py-0.5 rounded ${r.langs_present?.[lg] ? "bg-emerald-100 text-emerald-700" : "bg-[color:var(--gv-ink)]/5 text-[color:var(--gv-ink)]/30"}`}>{lg.toUpperCase()}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-[11.5px] text-[color:var(--gv-ink)]/55 font-mono">{r.slug}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {mergeModal && (
        <MergeModal modal={mergeModal}
          setPrimary={(slug) => setMergeModal({ ...mergeModal, primarySlug: slug })}
          onClose={() => setMergeModal(null)}
          onDone={async (res) => {
            setMergeModal(null);
            setFlash(`Fusion OK — primary: ${res.primary_slug} · ${res.local_docs_removed} docs supprimés`);
            await load();
          }}
        />
      )}
    </div>
  );
}

// ────────────────────── Merge Modal ──────────────────────

function MergeModal({ modal, setPrimary, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const kind = modal.kind;
  const rows = modal.rows || [];
  const primary = rows.find((r) => r.slug === modal.primarySlug) || rows[0];
  const duplicates = rows.filter((r) => r.slug !== modal.primarySlug);

  const confirm = async () => {
    if (!primary || duplicates.length === 0) return;
    setBusy(true); setError("");
    try {
      const res = await adminApi.atlasMerge({
        kind,
        primary_slug: primary.slug,
        duplicate_slugs: duplicates.map((r) => r.slug),
        delete_duplicates: true,
      });
      onDone(res);
    } catch (e) {
      setError(e.message || "Erreur fusion");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="merge-modal">
      <div className="bg-white rounded-[6px] w-full max-w-2xl mt-10 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[color:var(--gv-line)]">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1">Fusionner {rows.length} {KIND_LABELS[kind] || kind}s</div>
            <h3 className="font-display text-xl text-[color:var(--gv-ink)]">Choisis la fiche primaire</h3>
            <p className="text-[12.5px] text-[color:var(--gv-ink)]/60 mt-1">Les autres fiches seront supprimées. Les traductions manquantes seront récupérées depuis les doublons.</p>
          </div>
          <button onClick={onClose} className="text-[color:var(--gv-ink)]/55 hover:text-[color:var(--gv-ink)]"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-2">
          {rows.map((r) => (
            <label key={r.slug} className={`flex items-center gap-3 p-3 border rounded-[5px] cursor-pointer ${r.slug === primary?.slug ? "border-emerald-400 bg-emerald-50" : "border-[color:var(--gv-line)] hover:bg-[color:var(--gv-mist)]/30"}`} data-testid={`merge-pick-${r.slug}`}>
              <input type="radio" checked={r.slug === primary?.slug} onChange={() => setPrimary(r.slug)} className="accent-emerald-500"/>
              <div className="flex-1">
                <div className="font-medium text-[14px]">{r.name}</div>
                <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 font-mono">{r.slug}</div>
                <div className="text-[12px] text-[color:var(--gv-ink)]/60 mt-0.5">{r.region}</div>
              </div>
              <div className="flex gap-1">
                {LANGS.map((lg) => (
                  <span key={lg} className={`text-[10px] px-1.5 py-0.5 rounded ${r.langs_present?.[lg] ? "bg-emerald-100 text-emerald-700" : "bg-[color:var(--gv-ink)]/5 text-[color:var(--gv-ink)]/30"}`}>{lg.toUpperCase()}</span>
                ))}
              </div>
            </label>
          ))}
          {error && <div className="text-rose-600 text-[12.5px]">{error}</div>}
        </div>
        <div className="flex gap-2 justify-end p-5 border-t border-[color:var(--gv-line)]">
          <button onClick={onClose} className="text-[13px] px-4 py-2 border border-[color:var(--gv-line)] rounded-[4px] hover:bg-[color:var(--gv-mist)]" data-testid="merge-cancel">Annuler</button>
          <button onClick={confirm} disabled={busy || duplicates.length === 0}
            className="text-[13px] px-4 py-2 bg-indigo-600 text-white rounded-[4px] hover:opacity-90 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="merge-confirm">
            {busy ? <Loader2 size={13} className="animate-spin"/> : <GitMerge size={13}/>}
            Fusionner ({duplicates.length} supprimé{duplicates.length>1?"s":""})
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminAtlasIAPage;
